import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { ApiError } from '../types/auth.types';

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
} as const;

/**
 * Error Messages by Status Code
 */
export const ERROR_MESSAGES = {
  // Client Errors (4xx)
  [HTTP_STATUS.BAD_REQUEST]: 'Invalid request. Please check your input.',
  [HTTP_STATUS.UNAUTHORIZED]: 'Invalid credentials. Please try again.',
  [HTTP_STATUS.FORBIDDEN]: 'Access denied. You do not have permission.',
  [HTTP_STATUS.NOT_FOUND]: 'Resource not found. Please check your input.',
  [HTTP_STATUS.CONFLICT]: 'Conflict. This resource already exists.',
  [HTTP_STATUS.UNPROCESSABLE_ENTITY]: 'Unable to process request. Please check your data.',
  [HTTP_STATUS.TOO_MANY_REQUESTS]: 'Too many requests. Please wait a moment and try again.',

  // Server Errors (5xx)
  [HTTP_STATUS.INTERNAL_SERVER_ERROR]: 'Internal server error. Please try again later or contact support.',
  [HTTP_STATUS.BAD_GATEWAY]: 'Bad gateway. Please try again later.',
  [HTTP_STATUS.SERVICE_UNAVAILABLE]: 'Service unavailable. Please try again later.',
  [HTTP_STATUS.GATEWAY_TIMEOUT]: 'Gateway timeout. Please try again.',

  // Network Errors
  NETWORK_ERROR: 'Network error. Please check your internet connection.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

/**
 * Handle API errors and display appropriate messages
 * 
 * @param error - The error object from the API call
 * @param defaultMessage - Optional custom default message
 * @param showToast - Whether to show a toast notification (default: true)
 * @returns The error message string
 */
export const handleApiError = (
  error: unknown,
  defaultMessage?: string,
  showToast: boolean = true
): string => {
  let errorMessage: string;

  if (error instanceof AxiosError) {
    const statusCode = error.response?.status;
    const apiError = error.response?.data as ApiError;
    const errorCode = error.code;

    // Check for network errors (connection refused, timeout, etc.)
    if (
      !error.response || 
      errorCode === 'ERR_NETWORK' || 
      errorCode === 'ERR_CONNECTION_REFUSED' ||
      errorCode === 'ECONNREFUSED' ||
      errorCode === 'ETIMEDOUT' ||
      errorCode === 'ERR_NETWORK_TIMEOUT'
    ) {
      // Network error (no response from server, connection refused, timeout)
      errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
    } else if (apiError?.message || apiError?.error) {
      // Prioritize backend error message
      errorMessage = apiError.message || apiError.error || defaultMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
    } else if (statusCode && statusCode in ERROR_MESSAGES) {
      // Use predefined message for known status codes
      errorMessage = ERROR_MESSAGES[statusCode as keyof typeof ERROR_MESSAGES];
    } else {
      // Use default message or unknown error
      errorMessage = defaultMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
    }
  } else if (error instanceof Error) {
    // JavaScript Error
    errorMessage = error.message || defaultMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
  } else {
    // Unknown error type
    errorMessage = defaultMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  // Log error for debugging
  console.error('API Error:', error);
  console.error('Error Message:', errorMessage);

  // Show toast notification if enabled
  if (showToast) {
    toast.error(errorMessage);
  }

  return errorMessage;
};

/**
 * Handle API success messages
 * 
 * @param message - Success message to display
 * @param showToast - Whether to show a toast notification (default: true)
 */
export const handleApiSuccess = (
  message: string,
  showToast: boolean = true
): void => {
  console.log('API Success:', message);
  
  if (showToast) {
    toast.success(message);
  }
};

/**
 * Check if error is a specific HTTP status code
 * 
 * @param error - The error object
 * @param statusCode - The status code to check
 * @returns True if error matches the status code
 */
export const isErrorStatus = (
  error: unknown,
  statusCode: number
): boolean => {
  if (error instanceof AxiosError) {
    return error.response?.status === statusCode;
  }
  return false;
};

/**
 * Check if error is a network error
 * 
 * @param error - The error object
 * @returns True if it's a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    const errorCode = error.code;
    return (
      !error.response || 
      errorCode === 'ERR_NETWORK' || 
      errorCode === 'ERR_CONNECTION_REFUSED' ||
      errorCode === 'ECONNREFUSED' ||
      errorCode === 'ETIMEDOUT' ||
      errorCode === 'ERR_NETWORK_TIMEOUT'
    );
  }
  return false;
};

/**
 * Extract error message from API error without showing toast
 * 
 * @param error - The error object
 * @param defaultMessage - Optional custom default message
 * @returns The error message string
 */
export const getErrorMessage = (
  error: unknown,
  defaultMessage?: string
): string => {
  return handleApiError(error, defaultMessage, false);
};

